import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, withTheme } from 'styled-components';
import { DateSession, ProgramSession, ProgramTag, SelectedDates } from 'types/programs';
import { BlockTitleContainer, ProgramsContainer } from './styled';
import GradientTitle from 'components/gradient-title/GradientTitle';
import Tags from 'components/tags/Tags';
import {
    CurrentDate,
    DatesContent,
    DatesMonth,
    DatesNumber,
    DatesTag,
    DatesTagContent,
    DayOfWeek,
    MonthNumber,
    SessionsBlock,
    TagContainer,
} from './styled/programs';
import dayjs from 'dayjs';
import { Session } from './components';

interface ProgramsProps {
    theme: DefaultTheme;
    selectedDate: SelectedDates[];
    sessions: DateSession[];
    selectedTags: string[];
    setSelectedTags: (chosen: string[]) => void;
    tags: ProgramTag[];
    currentSessions: ProgramSession[];
    setCurrentSessions: (sessions: ProgramSession[]) => void;
    setSelectedDate: (selectedDates: SelectedDates[]) => void;
    setOpened: (id: string) => void;
    openedIds: Set<string>;
}

const Programs: FC<ProgramsProps> = ({
    theme,
    selectedDate,
    sessions,
    selectedTags,
    setSelectedTags,
    tags,
    currentSessions,
    setCurrentSessions,
    setSelectedDate,
    setOpened,
    openedIds,
}) => {
    const { t } = useTranslation('pages/home');

    const getDate = (): dayjs.Dayjs | undefined => {
        const date = selectedDate.find((r) => r.isSelected);

        if (!date) return;

        return dayjs(date.date);
    };

    return sessions.length ? (
        <ProgramsContainer id="programs">
            <BlockTitleContainer>
                <GradientTitle
                    variant="h1"
                    label={t('agenda-title')}
                    startColor="#BBFF33"
                    finishColor="#53F592"
                    startLabelColor={theme.palette.black}
                    finishLabelColor={theme.palette.white}
                />
            </BlockTitleContainer>
            <Tags
                selectedTags={selectedTags}
                onChange={(chosen) => {
                    setSelectedTags(chosen);
                }}
                tags={tags.map(({ id, name }) => ({ key: id, label: name }))}
            />
            <DatesContent>
                {currentSessions?.length ? (
                    <>
                        <CurrentDate>
                            <DayOfWeek>{getDate()?.format('dddd,')}</DayOfWeek>
                            <MonthNumber>{getDate()?.format('MMMM DD')}</MonthNumber>
                        </CurrentDate>
                        <DatesTagContent>
                            {selectedDate.map((m, index) => (
                                <DatesTag
                                    key={`dates-tag-${m.date}`}
                                    active={m.isSelected}
                                    onClick={() => {
                                        const newVal = selectedDate.map((g, i) => ({
                                            ...g,
                                            isSelected: i === index,
                                        }));
                                        setSelectedDate(newVal);
                                        setCurrentSessions(sessions[index].sessions);
                                    }}
                                >
                                    <TagContainer>
                                        <DatesNumber>{dayjs(m.date).format('DD')}</DatesNumber>
                                        <DatesMonth>{dayjs(m.date).format('MMMM')}</DatesMonth>
                                    </TagContainer>
                                </DatesTag>
                            ))}
                        </DatesTagContent>
                    </>
                ) : (
                    <CurrentDate>{t('nothing-was-found')}</CurrentDate>
                )}
            </DatesContent>
            <SessionsBlock>
                {currentSessions &&
                    currentSessions
                        .filter(
                            (f) =>
                                !selectedTags.length ||
                                f.programEvents.some((pe) => pe.tags.some((pt) => selectedTags.includes(pt.id)))
                        )
                        .map((s) => (
                            <Session
                                key={s.id}
                                session={s}
                                selectedTagsId={selectedTags}
                                select={(id, val) => {
                                    if (val) {
                                        setSelectedTags([...selectedTags, id]);
                                        return;
                                    }

                                    setSelectedTags([...selectedTags.filter((r) => r !== id)]);
                                }}
                                openedIds={openedIds}
                                setOpened={setOpened}
                            />
                        ))}
            </SessionsBlock>
        </ProgramsContainer>
    ) : null;
};

export default withTheme(Programs);
